import { Account } from '@fanduel/cpe-um-bundle/account/library';
import { jwtDecode } from 'jwt-decode';

import { hasLobbySessionCookies } from '@/unified-modules/account/annexes/cookie-session';
import { getAWAuthTokens } from '@/utilities/getters/sessionContextGetters';
import { Logger } from '@/utilities/logger/datadog/rumEvents/rumEvents';
import { DdEventCategory, DdEventType } from '@/utilities/logger/datadog/types';
import { useStore } from '@/store/useStore';

export type LoginPromptPhase = 'init' | 'refresh' | 'post_clear';

export type PromptAccountLoginOptions = {
  reason: string;
  phase: LoginPromptPhase;
  /** Snapshot before GW clears store flags; defaults to current store value. */
  hadActiveSessionBeforeClear?: boolean;
};

const getTokenTimingContext = (): {
  tokenExpiresAt?: string;
  secondsUntilExpiry?: number;
  sessionTokenExpired?: boolean;
} => {
  try {
    const { authToken } = getAWAuthTokens();
    if (!authToken) return {};

    const { exp } = jwtDecode<{ exp?: number }>(authToken);
    if (!exp) return {};

    const expiresAtMs = exp * 1000;
    const secondsUntilExpiry = Math.floor((expiresAtMs - Date.now()) / 1000);

    return {
      tokenExpiresAt: new Date(expiresAtMs).toISOString(),
      secondsUntilExpiry,
      sessionTokenExpired: secondsUntilExpiry <= 0,
    };
  } catch {
    return {};
  }
};

const buildLoginPromptContext = (options: PromptAccountLoginOptions) => {
  const { authToken, loginToken } = getAWAuthTokens();
  const hadActiveSessionBeforeClear =
    options.hadActiveSessionBeforeClear ??
    useStore.getState().userSessionContext.hasActiveSession;

  return {
    reason: options.reason,
    phase: options.phase,
    hadActiveSessionBeforeClear,
    hasAuthCookie: Boolean(authToken),
    hasLoginTokenCookie: Boolean(loginToken),
    hasLobbySessionCookies: hasLobbySessionCookies(),
    ...getTokenTimingContext(),
  };
};

/**
 * Opens Account UM login (`getSessionAsync('Normal')`) and emits
 * `[GW][SESSION] Login prompted` for Datadog RUM alerting.
 */
export const promptAccountLogin = (options: PromptAccountLoginOptions) => {
  Logger.logRumAction({
    name: '[GW][SESSION] Login prompted',
    context: buildLoginPromptContext(options),
    useCase: DdEventCategory.Session,
    eventType: DdEventType.Info,
  });

  return Account.getSessionAsync('Normal');
};
